import * as React from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
    Button,
    Popover as PopoverUI,
    List,
    ListItem,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Divider,
    useTheme,
    alpha
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { TOOLS_CONFIG } from '../../Setup/Config/tools-config';
import CodeIcon from '@mui/icons-material/Code';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import AppsIcon from '@mui/icons-material/Apps';

const Popover = styled(PopoverUI)(({ theme }) => ({
    '& .MuiPaper-root': {
        boxShadow: theme.shadows[8],
        borderRadius: theme.spacing(1),
        border: `1px solid ${theme.palette.divider}`,
        minWidth: 280,
        maxWidth: 320,
        marginTop: theme.spacing(1),
        zIndex: theme.zIndex.appBar + 1,
        padding: theme.spacing(1),
        '& .MuiListItemButton-root': {
            borderRadius: theme.spacing(0.5),
            marginBottom: theme.spacing(0.5),
            '&:hover': {
                backgroundColor: alpha(theme.palette.primary.main, 0.08),
            },
            '& .MuiListItemIcon-root': {
                minWidth: 36,
                '& svg': {
                    fontSize: '1.2rem',
                },
            },
        }
    }
}));

const StyledButton = styled(Button)(({ theme }) => ({
    color: theme.palette.text.primary,
    textTransform: 'none',
    fontWeight: theme.typography.fontWeightMedium,
    fontSize: '0.9375rem',
    padding: theme.spacing(0.75, 1.5),
    '& .MuiSvgIcon-root': {
        color: theme.palette.primary.main,
    },
    '&:hover': {
        backgroundColor: alpha(theme.palette.primary.main, 0.08),
        color: theme.palette.primary.main,
        '& .MuiSvgIcon-root': {
            color: theme.palette.primary.main,
        },
    },
}));

export const CategoryMenu = ({ category, label, icon: Icon, maxItems = 5, isMisc = false }) => {
    const navigate = useNavigate();
    const theme = useTheme();
    const [anchorEl, setAnchorEl] = React.useState(null);
    const open = Boolean(anchorEl);

    // Get tools from this category (featured first, then available)
    const categoryTools = React.useMemo(() => {
        if (isMisc) {
            // For General menu, get high-value tools from URL Tools, Text Processing, and Design Tools
            const miscCategories = ['URL Tools', 'Text Processing', 'Design Tools'];
            const highValueToolIds = [
                'url-encoder-decoder', // Featured URL tool
                'base64-encoder-decoder', // Featured Text tool
                'color-picker', // Featured Design tool
                'url-parser', // Popular URL tool
                'text-case-converter', // Popular Text tool
                'color-converter', // Popular Design tool
            ];
            
            return TOOLS_CONFIG
                .filter(tool => 
                    (miscCategories.includes(tool.category) || highValueToolIds.includes(tool.id)) &&
                    tool.available && 
                    tool.visibility === 'public'
                )
                .sort((a, b) => {
                    // Featured first
                    if (a.is_featured && !b.is_featured) return -1;
                    if (!a.is_featured && b.is_featured) return 1;
                    // Then high-value tools
                    const aIsHighValue = highValueToolIds.includes(a.id);
                    const bIsHighValue = highValueToolIds.includes(b.id);
                    if (aIsHighValue && !bIsHighValue) return -1;
                    if (!aIsHighValue && bIsHighValue) return 1;
                    return 0;
                })
                .slice(0, maxItems);
        }
        
        return TOOLS_CONFIG
            .filter(tool => 
                tool.category === category && 
                tool.available && 
                tool.visibility === 'public'
            )
            .sort((a, b) => {
                // Featured tools first
                if (a.is_featured && !b.is_featured) return -1;
                if (!a.is_featured && b.is_featured) return 1;
                return 0;
            })
            .slice(0, maxItems);
    }, [category, maxItems, isMisc]);

    const handleClick = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const handleToolClick = (route) => {
        navigate(route);
        handleClose();
    };

    const handleViewAll = () => {
        if (isMisc) {
            // For General, navigate to catalog with multiple categories
            navigate('/catalog');
        } else {
            navigate(`/catalog?category=${encodeURIComponent(category)}`);
        }
        handleClose();
    };

    if (categoryTools.length === 0) return null;

    return (
        <>
            <StyledButton
                onClick={handleClick}
                endIcon={<ArrowDropDownIcon />}
                startIcon={Icon ? <Icon /> : null}
            >
                {label}
            </StyledButton>
            <Popover
                open={open}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'left',
                }}
                disablePortal={false}
                slotProps={{
                    paper: {
                        sx: {
                            mt: 1,
                        }
                    }
                }}
            >
                <List dense sx={{ py: 0 }}>
                            {categoryTools.map((tool) => {
                                const ToolIcon = tool.icon;
                                return (
                                    <ListItem key={tool.id} disablePadding>
                                        <ListItemButton onClick={() => handleToolClick(tool.route)}>
                                            <ListItemIcon>
                                                <ToolIcon />
                                            </ListItemIcon>
                                            <ListItemText 
                                                primary={tool.title}
                                                primaryTypographyProps={{
                                                    fontSize: '0.875rem',
                                                }}
                                            />
                                        </ListItemButton>
                                    </ListItem>
                                );
                            })}
                            {categoryTools.length > 0 && (
                                <>
                                    <Divider sx={{ my: 1 }} />
                                    <ListItem disablePadding>
                                        <ListItemButton 
                                            onClick={handleViewAll}
                                            sx={{
                                                backgroundColor: alpha(theme.palette.primary.main, 0.08),
                                                '&:hover': {
                                                    backgroundColor: alpha(theme.palette.primary.main, 0.12),
                                                },
                                            }}
                                        >
                                            <ListItemIcon>
                                                <AppsIcon sx={{ color: theme.palette.primary.main }} />
                                            </ListItemIcon>
                                            <ListItemText 
                                                primary="View All"
                                                primaryTypographyProps={{
                                                    fontSize: '0.875rem',
                                                    fontWeight: theme.typography.fontWeightMedium,
                                                    color: theme.palette.primary.main,
                                                }}
                                            />
                                        </ListItemButton>
                                    </ListItem>
                                </>
                            )}
                        </List>
            </Popover>
        </>
    );
};

